/**
 * UploadedFilesGrid - AG Grid implementation for file list display
 *
 * Displays uploaded files in a sortable, filterable grid with actions.
 * Uses AG Grid Community edition with custom cell renderers for:
 * - File names with download links and quarantine badges
 * - Formatted dates
 * - Location (folder/subfolder paths)
 * - Action buttons (Delete)
 * - Review checkboxes (for Portway employees)
 */

import React, { useMemo } from "react";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { IconButton } from "@flexport/latitude-typescript";
import { QuarantineStatus } from "../generated/graphql";

// Register AG Grid v34 modules (required in v34+)
ModuleRegistry.registerModules([AllCommunityModule]);

// Configure AG Grid v34 theme using new Theming API
const gridTheme = themeQuartz;

/**
 * Grid wrapper with fixed height for consistent display
 * Height set to ~520px to show 12-15 rows without scrolling
 */
const GridWrapper = styled.div`
  width: 100%;
  height: 520px;
`;

/**
 * Container for file name and status badge display
 */
const FileNameWithStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/**
 * Quarantine status badge with color coding
 */
const StatusBadge = styled.span<{ status: QuarantineStatus }>`
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: white;
  background-color: ${(props) => {
    switch (props.status) {
      case QuarantineStatus.Clean:
        return "#28a745";
      case QuarantineStatus.Quarantined:
        return "#dc3545";
      case QuarantineStatus.ScanFailed:
        return "#fd7e14";
      default:
        return "#6c757d";
    }
  }};
`;

/**
 * Tooltip wrapper that properly shows tooltip when hovering over button and its children
 * The title attribute on the wrapper will trigger tooltips for the entire area
 */
const TooltipWrapper = styled.div`
  display: inline-flex;
  position: relative;
  cursor: pointer;

  /* Ensure button inherits the cursor and doesn't block tooltip */
  button {
    cursor: pointer;
  }
`;

/**
 * Actions container for button centering
 */
const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

/**
 * Review checkbox for Portway employee file reviews
 */
const ReviewCheckbox = styled.input.attrs({ type: "checkbox" })`
  cursor: pointer;
  margin-left: 8px;
  width: 18px;
  height: 18px;
`;

/**
 * File row data interface matching FileData from FileList
 */
export interface FileRow {
  id: string;
  fileName: string;
  extension: string;
  createdAt: string;
  fileUrl: string | null;
  folderPath: string | null;
  subfolderPath: string | null;
  reviewedByCurrentUser?: boolean;
  quarantineStatus: QuarantineStatus;
}

/**
 * Props for UploadedFilesGrid component
 */
interface UploadedFilesGridProps {
  /** Array of file rows to display */
  rows: ReadonlyArray<FileRow>;
  /** Whether current user can delete files */
  canDelete: boolean;
  /** Whether to show the reviewed column (Portway employees only) */
  showReviewed: boolean;
  /** Callback when delete button is clicked */
  onDelete: (id: string) => void;
  /** Callback when review checkbox is toggled (optional) */
  onToggleReview?: (id: string) => void;
}

/**
 * Date formatter for the createdAt column
 * Converts ISO string to localized date/time format
 */
const dateFormatter = (params: ValueFormatterParams<FileRow>): string => {
  const v = params.value;
  if (typeof v !== "string") return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleString();
};

/**
 * Location value getter - combines folder and subfolder paths
 */
const locationGetter = (params: ValueGetterParams<FileRow>): string => {
  const d = params.data as FileRow | undefined;
  const folderPath = d?.folderPath || null;
  const subfolderPath = d?.subfolderPath || null;
  if (!folderPath) return "—";
  return subfolderPath ? `${folderPath}/${subfolderPath}` : folderPath;
};

/**
 * Cell renderer for file name column
 * Displays download link and quarantine status badge
 */
const NameCellRenderer: React.FC<ICellRendererParams<FileRow>> = (params) => {
  const { data } = params;
  if (!data) return null;

  return (
    <FileNameWithStatus>
      {/* Hidden span for test selector compatibility */}
      <span
        data-testid={`file-row-${data.id}`}
        style={{ display: "none" }}
      ></span>
      <a
        href={data.fileUrl || "#"}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`file-download-link-${data.id}`}
        style={{ color: "#0066cc", textDecoration: "none" }}
      >
        {data.fileName}
      </a>
      {data.quarantineStatus === QuarantineStatus.Quarantined && (
        <StatusBadge status={data.quarantineStatus}>QUARANTINED</StatusBadge>
      )}
    </FileNameWithStatus>
  );
};

/**
 * Cell renderer for actions column
 * Displays delete button if user has permission
 */
const ActionsCellRenderer: React.FC<
  ICellRendererParams<FileRow> & {
    canDelete: boolean;
    onDelete: (id: string) => void;
  }
> = (params) => {
  const { data } = params;
  const canDelete = (params as any).canDelete;
  const onDelete = (params as any).onDelete;

  if (!data) return null;
  if (!canDelete) return null;

  return (
    <ActionsContainer>
      <TooltipWrapper title="Delete file">
        <IconButton
          iconName="trash"
          label="Delete"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="danger"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onDelete(data.id)}
          testId={`delete-file-${data.id}`}
        />
      </TooltipWrapper>
    </ActionsContainer>
  );
};

/**
 * Cell renderer for reviewed column
 * Displays checkbox for Portway employees to mark files as reviewed
 */
const ReviewedCellRenderer: React.FC<
  ICellRendererParams<FileRow> & {
    onToggleReview?: (id: string) => void;
  }
> = (params) => {
  const { data } = params;
  const onToggleReview = (params as any).onToggleReview;

  if (!data) return null;

  return (
    <ReviewCheckbox
      checked={!!data.reviewedByCurrentUser}
      onChange={() => onToggleReview && onToggleReview(data.id)}
      data-testid={`review-file-${data.id}`}
      title={
        data.reviewedByCurrentUser ? "Mark as not reviewed" : "Mark as reviewed"
      }
      aria-label={
        data.reviewedByCurrentUser ? "Mark as not reviewed" : "Mark as reviewed"
      }
    />
  );
};

/**
 * Add data-testid attributes to AG Grid rows for E2E testing
 * AG Grid doesn't natively support custom data attributes on rows,
 * so we add them manually after render
 */
const addTestIdsToRows = () => {
  // Find all AG Grid data rows (not header rows)
  const rowElements = document.querySelectorAll(
    '[data-testid="file-list-table"] [role="row"]:not(.ag-header-row)',
  );

  rowElements.forEach((rowElement) => {
    // Extract file ID from the row class (set by getRowClass)
    const classList = Array.from(rowElement.classList);
    const fileRowClass = classList.find((cls) => cls.startsWith("file-row-"));

    if (fileRowClass) {
      const fileId = fileRowClass.replace("file-row-", "");
      // Add data-testid attribute to the row
      rowElement.setAttribute("data-testid", `file-row-${fileId}`);
    }
  });
};

/**
 * UploadedFilesGrid Component
 *
 * Displays uploaded files in an AG Grid table with sorting, filtering,
 * and custom cell renderers for enhanced functionality.
 */
const UploadedFilesGrid: React.FC<UploadedFilesGridProps> = ({
  rows,
  canDelete,
  showReviewed,
  onDelete,
  onToggleReview,
}) => {
  /**
   * Column definitions for AG Grid
   * Defines structure, renderers, and behavior for each column
   */
  const columnDefs = useMemo<ColDef<FileRow>[]>(() => {
    const base: ColDef<FileRow>[] = [
      {
        headerName: "Name",
        field: "fileName",
        cellRenderer: NameCellRenderer,
        flex: 1,
        minWidth: 200,
        suppressHeaderMenuButton: true, // v34: Use suppressHeaderMenuButton instead of deprecated suppressMenu
      },
      {
        headerName: "Type",
        field: "extension",
        width: 110,
        filter: true,
        suppressHeaderMenuButton: true, // v34: Use suppressHeaderMenuButton instead of deprecated suppressMenu
      },
      {
        headerName: "Upload Date",
        field: "createdAt",
        valueFormatter: dateFormatter,
        sort: "desc", // Default sort by date descending (newest first)
        width: 200,
        suppressHeaderMenuButton: true, // v34: Use suppressHeaderMenuButton instead of deprecated suppressMenu
      },
      {
        headerName: "Location (Folder/Subfolder)",
        valueGetter: locationGetter,
        flex: 1,
        minWidth: 220,
        suppressHeaderMenuButton: true, // v34: Use suppressHeaderMenuButton instead of deprecated suppressMenu
      },
      {
        headerName: "Actions",
        sortable: false,
        filter: false,
        width: 100,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: { canDelete, onDelete },
        suppressHeaderMenuButton: true, // v34: Use suppressHeaderMenuButton instead of deprecated suppressMenu
      },
    ];

    // Add reviewed column if showReviewed flag is true
    if (showReviewed) {
      base.push({
        headerName: "Reviewed",
        field: "reviewedByCurrentUser",
        width: 120,
        sortable: false,
        filter: false,
        cellRenderer: ReviewedCellRenderer,
        cellRendererParams: { onToggleReview },
        suppressHeaderMenuButton: true, // v34: Use suppressHeaderMenuButton instead of deprecated suppressMenu
      });
    }

    return base;
  }, [canDelete, onDelete, onToggleReview, showReviewed]);

  /**
   * Default column configuration applied to all columns
   */
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true, // v34: Simplified menu control
    }),
    [],
  );

  return (
    <GridWrapper data-testid="file-list-table">
      <AgGridReact<FileRow>
        theme={gridTheme}
        rowData={rows as FileRow[]}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data.id}
        suppressCellFocus={false}
        // Add row class for E2E testing
        getRowClass={(params) =>
          params.data ? `file-row-${params.data.id}` : undefined
        }
        // Add data-testid attributes after rows render for E2E testing
        onFirstDataRendered={addTestIdsToRows}
        onModelUpdated={addTestIdsToRows}
        onPaginationChanged={addTestIdsToRows}
        pagination={true}
        paginationPageSize={50}
        paginationPageSizeSelector={[20, 50, 100, 999]}
      />
    </GridWrapper>
  );
};

export default UploadedFilesGrid;
